/*
 * One knowledge-base operation, from start to reviewable report.
 *
 * An operation is not a second kind of run: `POST .../operations` returns a
 * `session_id` and a `run_id`, and it streams down exactly the same event channel
 * as any other run. So this reuses `RunStream` untouched and adds only what an
 * operation needs on top — the interleaved transcript the chat renders, and the
 * commit report the ingest card is built from.
 *
 * Ownership matters: this lives in the workbench, not in the chat tab, so opening
 * the diff of a running ingest does not unmount the stream and lose the deltas.
 *
 * The report is fetched from the API after the run settles rather than parsed out
 * of the transcript. What changed on disk is the server's answer, and a card built
 * by scraping prose would be a plausible-looking guess at a reviewable artifact.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getRun, streamUrl } from '../../../api/client.ts'
import {
  getHistory,
  getOperationDiff,
  revertOperation,
  startOperation,
  type HistoryOperation,
  type OperationDiff,
  type OperationRequest,
} from '../../../api/knowledgeBases.ts'
import { deltaOf, RunStream, type StreamEnd, type StreamEnvelope } from '../../../api/stream.ts'

/**
 * How long the one-click undo stays offered after an operation settles.
 *
 * The affordance is a convenience with a visible countdown, not the recovery path:
 * revert remains available for any operation forever from the history tab. The
 * window exists so the button means "I have just read this and I want it gone",
 * which is a different intent from reverting something from last week.
 */
export const UNDO_WINDOW_MS = 120_000

const UNDO_TICK_MS = 1_000

export type StepKind = 'text' | 'thinking' | 'tool'

export interface TextStep {
  id: string
  kind: 'text' | 'thinking'
  text: string
}

export interface ToolStep {
  id: string
  kind: 'tool'
  toolCallId: string
  name: string
  arg: string
  state: 'running' | 'ok' | 'fail'
  detail: string
}

export type Step = TextStep | ToolStep

export type RunPhase = 'idle' | 'starting' | 'streaming' | 'reconciling' | 'settled' | 'failed'

export interface OperationReport {
  op: HistoryOperation | null
  diff: OperationDiff | null
  /** Set when the report could not be read; the card says so rather than lying. */
  problem: string | null
}

export interface ActiveOperation {
  opId: string
  sessionId: string
  runId: string
  request: OperationRequest
  /** The user's own words, rendered as the message that started the operation. */
  prompt: string
  /**
   * False when the server granted the operation with `wiki/` read-only — another
   * run held the write lease when it started. Worth a visible notice: an ingest
   * that cannot write will read as "the agent did nothing" otherwise.
   */
  wikiWritable: boolean
}

export interface OperationRun {
  phase: RunPhase
  active: ActiveOperation | null
  steps: Step[]
  /** Run status once reconciled against `GET /v1/runs/{id}`. */
  runStatus: string | null
  report: OperationReport | null
  error: string | null
  /** Milliseconds left on the undo window, or null when it is not offered. */
  undoLeftMs: number | null
  reverting: boolean
  start: (request: OperationRequest, prompt: string) => Promise<void>
  revert: () => Promise<void>
  dismissUndo: () => void
  clear: () => void
}

export function useOperationRun(kbId: string, onSettled: () => void): OperationRun {
  const [phase, setPhase] = useState<RunPhase>('idle')
  const [active, setActive] = useState<ActiveOperation | null>(null)
  const [steps, setSteps] = useState<Step[]>([])
  const [runStatus, setRunStatus] = useState<string | null>(null)
  const [report, setReport] = useState<OperationReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [undoUntil, setUndoUntil] = useState<number | null>(null)
  const [now, setNow] = useState(() => Date.now())
  const [reverting, setReverting] = useState(false)
  const streamRef = useRef<RunStream | null>(null)
  const settledRef = useRef(onSettled)
  settledRef.current = onSettled

  useEffect(() => () => streamRef.current?.close(), [])

  // Switching bases abandons whatever was on screen; the stream belongs to the
  // base it was started against.
  useEffect(() => {
    streamRef.current?.close()
    streamRef.current = null
    setPhase('idle')
    setActive(null)
    setSteps([])
    setReport(null)
    setRunStatus(null)
    setError(null)
    setUndoUntil(null)
  }, [kbId])

  useEffect(() => {
    if (undoUntil === null) return
    const timer = setInterval(() => setNow(Date.now()), UNDO_TICK_MS)
    return () => clearInterval(timer)
  }, [undoUntil])

  const undoLeftMs = useMemo(() => {
    if (undoUntil === null) return null
    const left = undoUntil - now
    return left > 0 ? left : null
  }, [undoUntil, now])

  const loadReport = useCallback(
    async (opId: string) => {
      let op: HistoryOperation | null = null
      let diff: OperationDiff | null = null
      const problems: string[] = []
      try {
        const history = await getHistory(kbId)
        op = (history.ops ?? []).find((candidate) => candidate.op_id === opId) ?? null
        if (!op) problems.push('операции нет в истории')
      } catch (err) {
        problems.push(`история: ${err instanceof Error ? err.message : 'ошибка'}`)
      }
      try {
        diff = await getOperationDiff(kbId, opId)
      } catch (err) {
        problems.push(`дифф: ${err instanceof Error ? err.message : 'ошибка'}`)
      }
      setReport({ op, diff, problem: problems.length > 0 ? problems.join(' · ') : null })
    },
    [kbId],
  )

  const start = useCallback(
    async (request: OperationRequest, prompt: string) => {
      streamRef.current?.close()
      setPhase('starting')
      setSteps([])
      setReport(null)
      setRunStatus(null)
      setError(null)
      setUndoUntil(null)

      let started
      try {
        started = await startOperation(kbId, request)
      } catch (err) {
        setPhase('failed')
        setError(err instanceof Error ? err.message : 'не удалось запустить операцию')
        return
      }

      const operation: ActiveOperation = {
        opId: started.op_id,
        sessionId: started.session_id,
        runId: started.run_id,
        request,
        prompt,
        wikiWritable: started.wiki_writable !== false,
      }
      setActive(operation)
      setPhase('streaming')

      const finish = async (end: StreamEnd) => {
        if (end.reason === 'unauthorized') {
          setPhase('failed')
          setError('сессия истекла')
          return
        }
        setPhase('reconciling')
        try {
          const run = await getRun(operation.runId)
          setRunStatus(run.stop_reason ? `${run.status} · ${run.stop_reason}` : run.status)
        } catch {
          // A stream that ended is not proof of anything either way; if the run
          // cannot be read, say which signal we do have rather than inventing one.
          setRunStatus(end.reason === 'terminal' ? 'completed' : `поток: ${end.reason}`)
        }
        await loadReport(operation.opId)
        setPhase('settled')
        setUndoUntil(Date.now() + UNDO_WINDOW_MS)
        setNow(Date.now())
        settledRef.current()
      }

      const stream = new RunStream({
        url: streamUrl(operation.runId),
        onEvent: (envelope) => setSteps((current) => reduce(current, envelope)),
        onEnd: (end) => void finish(end),
      })
      streamRef.current = stream
      void stream.start()
    },
    [kbId, loadReport],
  )

  const revert = useCallback(async () => {
    const opId = active?.opId
    if (!opId) return
    setReverting(true)
    try {
      await revertOperation(kbId, opId)
      setUndoUntil(null)
      settledRef.current()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось откатить операцию')
    } finally {
      setReverting(false)
    }
  }, [active?.opId, kbId])

  const dismissUndo = useCallback(() => setUndoUntil(null), [])

  const clear = useCallback(() => {
    streamRef.current?.close()
    streamRef.current = null
    setPhase('idle')
    setActive(null)
    setSteps([])
    setReport(null)
    setRunStatus(null)
    setError(null)
    setUndoUntil(null)
  }, [])

  return {
    phase,
    active,
    steps,
    runStatus,
    report,
    error,
    undoLeftMs,
    reverting,
    start,
    revert,
    dismissUndo,
    clear,
  }
}

// ── the reducer ───────────────────────────────────────────────────────────────

/**
 * Fold one event into the transcript.
 *
 * Text is appended to the trailing text step so a paragraph stays a paragraph, and
 * a tool event closes it — which is what reproduces the real shape of a run:
 * prose, a group of tool calls, more prose.
 */
export function reduce(steps: Step[], envelope: StreamEnvelope): Step[] {
  // Subagent chatter belongs to a nested run and would interleave unreadably with
  // the leader's own transcript.
  if (envelope.stream_scope === 'subagent') return steps

  const delta = deltaOf(envelope)
  if (delta) {
    const last = steps[steps.length - 1]
    if (last && last.kind === delta.kind) {
      const updated = { ...last, text: last.text + delta.text }
      return [...steps.slice(0, -1), updated]
    }
    return [...steps, { id: envelope.event_id, kind: delta.kind, text: delta.text }]
  }

  const payload = envelope.payload as Record<string, unknown>
  const toolCallId = typeof payload.tool_call_id === 'string' ? payload.tool_call_id : null

  if (envelope.event_type === 'tool_use_start' && toolCallId) {
    const name = typeof payload.tool_name === 'string' ? payload.tool_name : 'tool'
    return [
      ...steps,
      { id: envelope.event_id, kind: 'tool', toolCallId, name, arg: '', state: 'running', detail: '' },
    ]
  }

  if (envelope.event_type === 'tool_use_stop' && toolCallId) {
    const arg = toolArgument(payload.final_input)
    return patchTool(steps, toolCallId, (step) => ({ ...step, arg: arg || step.arg }))
  }

  if (envelope.event_type === 'tool_result' && toolCallId) {
    const ok = payload.success !== false
    const detail = resultDetail(payload)
    return patchTool(steps, toolCallId, (step) => ({
      ...step,
      state: ok ? 'ok' : 'fail',
      detail,
      name: typeof payload.tool_name === 'string' ? payload.tool_name : step.name,
    }))
  }

  return steps
}

function patchTool(steps: Step[], toolCallId: string, patch: (step: ToolStep) => ToolStep): Step[] {
  let touched = false
  const next = steps.map((step) => {
    if (step.kind !== 'tool' || step.toolCallId !== toolCallId) return step
    touched = true
    return patch(step)
  })
  return touched ? next : steps
}

/**
 * The field of a tool's input worth showing on one line, most informative first.
 *
 * Order matters where a call carries several: a Grep has both `pattern` and `path`,
 * and the pattern is the thing the reader is trying to see — showing the directory
 * turns every search in the transcript into the same uninformative row.
 */
const ARG_FIELDS = [
  'file_path',
  'notebook_path',
  'pattern',
  'path',
  'command',
  'query',
  'url',
  'question',
  'description',
  'prompt',
]

export function toolArgument(input: unknown): string {
  const value = typeof input === 'string' ? safeParse(input) : input
  if (typeof value === 'string') return truncate(value)
  if (!value || typeof value !== 'object') return ''
  const record = value as Record<string, unknown>
  for (const field of ARG_FIELDS) {
    const candidate = record[field]
    if (typeof candidate === 'string' && candidate.length > 0) return truncate(candidate)
  }
  const first = Object.values(record).find((entry) => typeof entry === 'string')
  return typeof first === 'string' ? truncate(first) : ''
}

function resultDetail(payload: Record<string, unknown>): string {
  const error = payload.error
  if (typeof error === 'string' && error.length > 0) return truncate(error, 90)
  const duration = payload.duration_ms
  if (typeof duration === 'number' && duration >= 0) {
    return duration >= 1000 ? `${(duration / 1000).toFixed(1).replace('.', ',')} с` : `${duration} мс`
  }
  return ''
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

function truncate(text: string, limit = 120): string {
  const flat = text.replace(/\s+/g, ' ').trim()
  return flat.length > limit ? `${flat.slice(0, limit - 1)}…` : flat
}
